import React, { useState } from "react";
import { GET_APPLE_TV } from "../queries/appleTVQueries";
import DeviceDetailBase, { formatMacAddress } from "../components/DeviceDetailBase";
import {
  updateDeviceInventory,
  restartDevice,
} from "../commands/mdmCommands.js";
import RenameDeviceModal from "../components/modals/RenameDeviceModal.jsx";
import InstallProfileModal from "../components/modals/InstallProfileModal.jsx";
import EraseDeviceModal from "../components/modals/EraseDeviceModal.jsx";
import TimeZoneModal from "../components/modals/TimeZoneModal.jsx";
import ShutdownDeviceModal from "../components/modals/ShutdownDeviceModal.jsx";

export default function AppleTVDetail() {
  const [showRenameDeviceModal, setShowRenameDeviceModal] = useState(false);
  const [showInstallProfileModal, setShowInstallProfileModal] = useState(false);
  const [showEraseDeviceModal, setShowEraseDeviceModal] = useState(false);
  const [showTimeZoneModal, setShowTimeZoneModal] = useState(false);
  const [showShutdownDeviceModal, setShowShutdownDeviceModal] = useState(false);

  const actionButtons = [
    {
      label: "Erase Device",
      capability: "canErase",
      onClick: () => setShowEraseDeviceModal(true)
    },
    {
      label: "Install Profile",
      capability: "canInstallProfile",
      onClick: () => setShowInstallProfileModal(true)
    },
    {
      label: "Rename Device",
      onClick: () => setShowRenameDeviceModal(true)
    },
    {
      label: "Restart Device",
      capability: "canRestart",
      onClick: (device) => restartDevice(device.UDID)
    },
    {
      label: "Herunterfahren",
      capability: "canShutdown",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      onClick: () => setShowShutdownDeviceModal(true),
    },
    {
      label: "Zeitzone",
      capability: "canTimeZone",
      onClick: () => setShowTimeZoneModal(true)
    }
  ];

  const infoSections = [
    {
      title: 'hardware',
      getData: (device) => ({
        "serial number": device.SerialNumber,
        "product name": device.QueryResponses.ProductName,
        // "storage": calculateStoragePercentage(device) // not available on tvOS devices
      })
    },
    {
      title: 'network',
      getData: (device) => {
        const deviceId = device.QueryResponses?.DeviceID?.toUpperCase?.() || '';
        const wifiMac = device.QueryResponses?.WiFiMAC?.toUpperCase?.() || '';
        const isWifiOnly = deviceId && wifiMac && deviceId === wifiMac;
        return {
          "WiFi MAC address": formatMacAddress(device.QueryResponses?.WiFiMAC),
          "bluetooth MAC address": formatMacAddress(device.QueryResponses?.BluetoothMAC),
          "connection type": isWifiOnly ? "WiFi" : "Ethernet",
          "Ethernet MAC address": isWifiOnly ? "—" : formatMacAddress(device.QueryResponses?.DeviceID)
        };
      }
    },
    {
      title: 'operating system',
      getData: (device) => ({
        "tvOS version": device.QueryResponses?.OSVersion,
        "build version": device.QueryResponses?.BuildVersion,
        "MDM profile installed": device.mdmProfileInstalled,
        "supervised": device.QueryResponses?.IsSupervised,
        "DEP supervised (Soll)": device.enrollmentContext?.depIsSupervised == null
          ? "—"
          : (device.enrollmentContext.depIsSupervised ? "ja" : "nein"),
        "Zeitzone": device.QueryResponses?.TimeZone || "—"
      })
    }
  ];

  const renderModals = (device, configProfiles, onCommandIssued) => (
    <>
      {showRenameDeviceModal && (
        <RenameDeviceModal
          visible={showRenameDeviceModal}
          UDID={device.UDID}
          platform='tvOS'
          oldName={device.QueryResponses?.DeviceName}
          hideRenameDeviceModal={() => setShowRenameDeviceModal(false)}
        />
      )}
      {showInstallProfileModal && (
        <InstallProfileModal
          visible={showInstallProfileModal}
          UDID={device.UDID}
          currentProfiles={device.Profiles || []}
          configProfiles={configProfiles || []}
          hideInstallProfileModal={() => setShowInstallProfileModal(false)}
        />
      )}
      {showEraseDeviceModal && (
        <EraseDeviceModal
          visible={showEraseDeviceModal}
          UDID={device.UDID}
          platform="appletv"
          device={device}
          configProfiles={configProfiles || []}
          hideEraseDeviceModal={() => setShowEraseDeviceModal(false)}
        />
      )}
      {showTimeZoneModal && (
        <TimeZoneModal
          visible={showTimeZoneModal}
          device={device}
          hideModal={() => setShowTimeZoneModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showShutdownDeviceModal && (
        <ShutdownDeviceModal
          visible={showShutdownDeviceModal}
          UDID={device.UDID}
          hideShutdownDeviceModal={() => setShowShutdownDeviceModal(false)}
        />
      )}
    </>
  );

  return (
    <DeviceDetailBase
      query={GET_APPLE_TV}
      deviceType="appletv"
      getDeviceData={(data) => data.appletv}
      actionButtons={actionButtons}
      renderModals={renderModals}
      infoSections={infoSections}
      onRefresh={(device) => updateDeviceInventory("tvos", device.UDID)}
    />
  );
}
